import { useCallback, useEffect, useState } from "react"
import { Editor as TiptapEditor } from "@tiptap/react"

import { escapeHTML } from "utils/escapeHtml"
import type { IfTriggerEventDetail } from "features/QuestionnaireEditor/components/Editor/LogicWizard"
import type { RecodeTriggerEventDetail } from "features/QuestionnaireEditor/components/Editor/RecodeWizard"

interface WizardState {
  isOpen: boolean
  initialText: string | null
  targetFrom: number
  targetTo: number
}

interface WizardControls {
  /** Current wizard state, or null when the wizard is closed */
  state: WizardState | null
  /** Closes the wizard (resets state to null) */
  close: () => void
  /** Replaces the targeted paragraph(s) with the wizard's output */
  apply: (text: string) => void
}

interface UseEditorWizardsProps {
  /** The TipTap editor instance */
  editor: TiptapEditor | null
}

interface UseEditorWizardsReturn {
  logicWizard: WizardControls
  recodeWizard: WizardControls
}

/**
 * Shared trigger wiring for the Logic and Recode wizards. Both are driven by
 * their trigger extensions via window CustomEvents whose details carry
 * { mode, lineText, from, to }, and both apply their output the same way.
 */
const useWizardTrigger = (
  editor: TiptapEditor | null,
  eventName: string,
): WizardControls => {
  // Wizard state — driven by the trigger extension via custom events.
  const [state, setState] = useState<WizardState | null>(null)

  // Listen for trigger-extension dispatches and open the wizard.
  useEffect(() => {
    if (!editor) return
    const onOpen = (e: Event) => {
      const detail = (
        e as CustomEvent<IfTriggerEventDetail | RecodeTriggerEventDetail>
      ).detail
      const initialText = detail.mode === "edit" ? detail.lineText : null
      setState({
        isOpen: true,
        initialText,
        targetFrom: detail.from,
        targetTo: detail.to,
      })
    }
    window.addEventListener(eventName, onOpen as EventListener)
    return () => {
      window.removeEventListener(eventName, onOpen as EventListener)
    }
  }, [editor, eventName])

  /**
   * Replace the targeted paragraph(s) with the wizard's output. The output
   * may contain multiple `\n`-separated lines (IF + body); each becomes its
   * own paragraph. Positions are paragraph-node bounds emitted by the
   * trigger extension's listParagraphs helper.
   */
  const apply = useCallback(
    (text: string) => {
      if (!editor || !state) return
      const { targetFrom, targetTo } = state
      const html = text
        .split("\n")
        .map((line) => `<p>${escapeHTML(line)}</p>`)
        .join("")
      editor
        .chain()
        .focus()
        .insertContentAt({ from: targetFrom, to: targetTo }, html)
        .run()
    },
    [editor, state],
  )

  const close = () => setState(null)

  return { state, close, apply }
}

/**
 * useEditorWizards wires the Logic Wizard (IfTriggerExtension) and the
 * Recode Wizard (RecodeTriggerExtension) custom-event triggers to local
 * wizard state, and exposes close/apply handlers for each.
 */
export const useEditorWizards = ({
  editor,
}: UseEditorWizardsProps): UseEditorWizardsReturn => {
  // Logic Wizard — driven by the IfTriggerExtension via custom events.
  const logicWizard = useWizardTrigger(editor, "dataq:open-logic-wizard")

  // Recode Wizard — driven by the RecodeTriggerExtension via custom events.
  const recodeWizard = useWizardTrigger(editor, "dataq:open-recode-wizard")

  return { logicWizard, recodeWizard }
}
